<script lang="ts">
    import route from '@/Util/routes';
    import { getRequest, kgToLb, lbToKg } from '@/Util/utils';
    import { LocalDate } from '@/time';
    import {isValid, format} from 'date-fns';
    import CommentThreadPanel from '@/Comments/CommentThreadPanel.svelte';
    import { getDayComments, postDayComment, markDayCommentsRead } from '@/Comments/api';
    import type { MyFitnessStores } from '../stores';

    // Only used to keep the calendar's per-day comment badges (CalendarCard) in step with what
    // happens in this modal's thread -- reading it, and posting into it.
    export let myFitness: MyFitnessStores | null = null;
    export let username;
    export let notes;
    export let today;

    //@ts-ignore
    const jq = window.$;
    const freshnessSliderWidth = 100;
    const sliderRange = 120;
    const thumbWidth = 24;

    //@ts-ignore
    let displayUnits = Xert._displayUnits;
    let dateString = (date: Date) => isValid(date) ? format(date, 'EEEE MMMM do yyyy') : '';
    let date: Date;
    let notesForm: HTMLFormElement;
    let noteContent = '';
    let freshnessVal = 0;
    let weightInput;
    let updateWeight;
    let thumbPosition;
    let positionOffset;

    const weightUnits = displayUnits == "imperial" ? "lb" : "kg"

    $: if (notesForm) notesForm.addEventListener('submit', (event)=>{event.preventDefault();});

    $: dayKey = isValid(date) ? LocalDate.format(date) : null;

    $: {
        thumbPosition = ((freshnessVal - (-60)) / sliderRange) * 100;
        positionOffset = Math.round(thumbWidth * thumbPosition / 100) - (thumbWidth / 2);
        thumbPosition *= freshnessSliderWidth / 100;
    }

    export function initNotesModal(event) {
        date = event.detail.date;
        initTodaysNotes();
        jq('#notes-modal').modal('show');
    }

    function initTodaysNotes() {
        let todays_notes = (notes != null) ? notes[LocalDate.format(date)] : null;
        if (todays_notes != null && todays_notes != undefined) {
            noteContent       = todays_notes.notes ?? '';
            updateWeight      = todays_notes.update_weight;
            freshnessVal      = todays_notes.freshness_feedback;
            if (updateWeight && todays_notes.weight != null)
                weightInput.value = (displayUnits == 'imperial' ? kgToLb(todays_notes.weight) : todays_notes.weight).toFixed(1);
        }
        else {
            noteContent = '';
            updateWeight = false;
            weightInput.value = '';
        }
    }

    function submitNotes() {
        if (updateWeight && !checkWeight()) return;

        const res = new FormData(notesForm);
        res.append('date', date.toISOString());
        res.append('forUser', username);
        res.append('updateWeight', updateWeight);

        jq('#notes-modal').modal('hide');
        window.fetch(route('CalendarController@saveNotesUpdates'), {
            ...getRequest(),
            method: 'POST',
            body: JSON.stringify(Object.fromEntries(res))
        }).then(()=>{
            let event = new CustomEvent("reloadEvents", {
                bubbles: true
            });
            document.getElementById('calendar').dispatchEvent(event);
        });

        updateLocalNotes(noteContent.length == 0 && !updateWeight, updateWeight);
    }

    function clearNotes() {
        updateWeight = false;
        noteContent = '';
    }

    function updateLocalNotes(removeNote, updateWeight) {
        const date_string = LocalDate.format(date)
        if (!removeNote) { 
            let todays_notes = (notes != null) ? notes[date_string] : null;
            if (todays_notes == null || todays_notes == undefined) 
                todays_notes = notes[date_string] = new Object;

            todays_notes.notes = noteContent;
            todays_notes.weight = Number.parseFloat((displayUnits == 'imperial' ? lbToKg(weightInput.value) : weightInput.value));
            todays_notes.update_weight = updateWeight;
        }
        else {
            if (notes[date_string]) 
                delete notes[date_string];
        }
        notes = notes; // trigger reactivity update
    }

    function checkWeight() {
        let valid;
        if (weightInput.value == '' || weightInput.value <= 0) {
            weightInput.setCustomValidity('Please input a valid weight');
            valid = false;
        }
        else {
            weightInput.setCustomValidity('');
            valid = true;
        }
        weightInput.reportValidity();
        return valid;
    }
</script>

<div class="modal fade" id="notes-modal" tabindex="-1" role="dialog" aria-labelledby="myModalLabel">
    <div class="modal-dialog modal-lg" role="document" style="max-width: 704px;">
        <div class="modal-content" style="display: flex; flex-direction: column; height: 100%;">
            <div class="modal-header">
                <div class="modal-title" id="modal-title">Notes and Updates - {dateString(date)}</div>
                <button type="button" class="modal-close" data-dismiss="modal" aria-label="Close"><span aria-hidden="true">&times;</span></button>
            </div>
            <div class="modal-body" style="padding-top: 15px;">
                <form bind:this={notesForm}>
                    <div class="row">
                        <label for="update-notes">Notes</label>
                        <textarea id="update-notes" name="notes" bind:value={noteContent}/>
                    </div>
                    <div class="row"> 
                        <div class="updates-segment">
                            <div class="updates-title">
                                Weight
                                <div class="toggle-wrapper">
                                    <input type="checkbox" id="update-weight" bind:checked={updateWeight} />
                                    <label for="update-weight"/>
                                </div>
                            </div>
                            <div class="updates-wrapper" hidden={!updateWeight}>
                                <div id="weight-container" style="display: flex; gap: 8px; align-items: center; margin-top: 16px">
                                    <input class='inputfield' type="number" name="weight" min="1" step="0.1" on:input={checkWeight} bind:this={weightInput}>
                                    <input type="hidden" name="weight_units" value={weightUnits}>
                                    <div style="font-size: 12px; color: #979797;">{weightUnits}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </form>
                {#if dayKey && Xert._hasMessagingFeature}
                {@const day = dayKey}
                <div class="row comments-row">
                    <label>Comments</label>
                    {#key day}
                        <CommentThreadPanel
                            load={(before) => getDayComments(day, username, before)}
                            send={async (text, files, references, replyToId) => {
                                const posted = await postDayComment(day, text, username, files, references, replyToId);
                                myFitness?.addDayComment(day);
                                return posted;
                            }}
                            markRead={() => markDayCommentsRead(day, username)}
                            onRead={() => myFitness?.markDayCommentsRead(day)}
                        />
                    {/key}
                </div>
                {/if}
            </div>
            <div class="modal-footer">
                <button value="save" type="submit" class="btn btn-default pull-right" data-toggle="tooltip" title="Save" name="submit-notes" id="submit-notes" on:click={submitNotes}>Save</button>
                <button class="btn btn-default pull-right" style="margin-right: 10px;" on:click={clearNotes} disabled={(noteContent.length == 0 && !updateWeight)}>Clear</button>
            </div>
        </div>
    </div>
</div>

<style lang="scss">
    #notes-modal {
        // .modal-content's inline height:100% has no effect on its own (.modal-dialog isn't
        // itself height-constrained) -- give the dialog a real bounded height and make
        // .modal-body the scrolling region, so a long comment thread scrolls the modal
        // instead of growing it past the viewport.
        .modal-dialog {
            display: flex;
            align-items: center;
            min-height: calc(100% - 60px);
        }
        .modal-content {
            max-height: 85vh;
        }
        .modal-body {
            overflow-y: auto;
            flex: 1 1 auto;
            min-height: 0;
        }
        .row {margin: 0;}
        .form-group {
            padding: 0;
            margin: 0;
        }
    }
    #update-notes {
        display: block;
        border: 1px solid #979797;
        border-radius: 3px;
        width: 100%;
        height: 199px;
    }
    label[for="update-notes"] {
        display: block;
    }
    .updates-segment {
        margin: 24px 0px;
    }
    .comments-row {
        margin-top: 24px;
        label {
            display: block;
        }
    }
    .updates-title {
        line-height: 24px;
        color: #3d3d3d;
        letter-spacing: 0.11px;
        display: flex;
        justify-content: space-between;
    }
    .btn.btn-default {
        padding: 8px 30px;
        border-radius: 4px;
        font-family: 'Roboto';
        font-weight: 400;
        line-height: 19px;
        text-align: center;
        letter-spacing: 0.48px;
        float: none;
        &[type="submit"] {
            background: #2F7076;
            color: white;
            &:hover {
                background: #204d51;
            }
        }
        &:disabled {
            background-color: #eeeeee;
            cursor: not-allowed;
        }
    }
    textarea {
        resize: vertical;
    }
    .inputfield {
        border: solid 1px #979797;
        border-radius: 3px;
    }
</style>